import React, { useEffect, useState } from 'react';
import { Crown, Loader2, Upload, CheckCircle, MessageCircle, Send as SendIcon, Music2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MEMBER_NAV } from '../../constants/nav';
import { useAuth } from '../../features/auth/AuthContext';
import {
  fetchVipPrograms,
  fetchMyVipApplications,
  createVipApplication,
  uploadVipFile,
  fetchMyVipGroup,
  VipApplicationPayload,
} from '../../features/vip/api/vip';
import type { VipProgram, VipApplication, VipGroupInfo } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ImageUpload } from '../../components/common/ImageUpload';
import { fetchMemberBrokers, Broker } from '../../services/api/brokers';
import { VIP } from '../../constants/testIds';

const inputClass =
  'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

const emptyForm: VipApplicationPayload = {
  vip_program_id: '',
  broker_name: '',
  account_number: '',
  account_name: '',
  submitted_equity: 0,
  tradingview_username: '',
  member_note: '',
};

export default function MemberVipPage() {
  const { user, refreshUser } = useAuth();
  const [programs, setPrograms] = useState<VipProgram[]>([]);
  const [applications, setApplications] = useState<VipApplication[]>([]);
  const [group, setGroup] = useState<VipGroupInfo | null>(null);
  const [detailProgram, setDetailProgram] = useState<VipProgram | null>(null);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<VipApplicationPayload>(emptyForm);
  const [proofUrl, setProofUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [p, a, g] = await Promise.all([fetchVipPrograms(), fetchMyVipApplications(), fetchMyVipGroup().catch(() => null)]);
    setPrograms(p);
    setApplications(a);
    setGroup(g);
    fetchMemberBrokers().then(setBrokers).catch(() => {});
    if (p.length && !form.vip_program_id) setForm((f) => ({ ...f, vip_program_id: p[0].id }));
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActiveVip = user?.vip_status === 'active' || user?.is_vip === true;
  const hasActiveFlow = isActiveVip || applications.some((a) => a.status === 'pending' || a.status === 'review');

  const missingField = (): string | null => {
    if (!form.vip_program_id) return 'Pilih program VIP';
    if (!form.broker_name.trim()) return 'Broker wajib diisi';
    if (!form.account_number.trim()) return 'No. Akun wajib diisi';
    if (!form.account_name.trim()) return 'Nama Akun wajib diisi';
    if (!(Number(form.submitted_equity) > 0)) return 'Equity wajib diisi (lebih dari 0)';
    if (!form.tradingview_username.trim()) return 'Username TradingView wajib diisi';
    return null;
  };
  const formInvalid = missingField() !== null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = missingField();
    if (err) return toast.error('Lengkapi form', { description: err });
    setSubmitting(true);
    try {
      const app = await createVipApplication(form);
      if (proofUrl.trim()) {
        try {
          await uploadVipFile(app.id, proofUrl.trim());
        } catch {
          /* non-blocking */
        }
      }
      toast.success('Pengajuan VIP terkirim', { description: 'Menunggu review admin.' });
      setForm({ ...emptyForm, vip_program_id: programs[0]?.id || '' });
      setProofUrl('');
      await load();
      await refreshUser();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      toast.error('Gagal mengajukan', { description: typeof detail === 'string' ? detail : err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="VIP Membership">
      <div className="max-w-5xl" data-testid={VIP.memberPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <Crown className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Membership</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">VIP Membership</h2>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Programs + Form */}
            <div className="lg:col-span-2 space-y-6">
              {isActiveVip && group && (
                <section data-testid={VIP.groupCard}>
                  <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Grup VIP</h3>
                  <div className="bg-gradient-to-br from-cyan-400/[0.07] to-transparent border border-cyan-400/25 rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
                      <p className="text-white font-medium">Selamat datang di {group.program_name || 'komunitas VIP'}!</p>
                    </div>
                    {group.join_instructions && (
                      <p className="text-sm text-white/70 whitespace-pre-line mb-4">{group.join_instructions}</p>
                    )}
                    {(group.whatsapp_group_url || group.telegram_group_url || group.tiktok_url || group.discord_group_url) ? (
                      <div className="flex flex-wrap gap-3">
                        {group.whatsapp_group_url && (
                          <a href={group.whatsapp_group_url} target="_blank" rel="noreferrer" data-testid={VIP.groupWhatsapp} className="flex items-center gap-2 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-emerald-500/25">
                            <MessageCircle className="w-4 h-4" strokeWidth={1.8} /> WhatsApp Grup
                          </a>
                        )}
                        {group.telegram_group_url && (
                          <a href={group.telegram_group_url} target="_blank" rel="noreferrer" data-testid={VIP.groupTelegram} className="flex items-center gap-2 bg-sky-500/15 text-sky-300 border border-sky-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-sky-500/25">
                            <SendIcon className="w-4 h-4" strokeWidth={1.8} /> Telegram Grup
                          </a>
                        )}
                        {group.tiktok_url && (
                          <a href={group.tiktok_url} target="_blank" rel="noreferrer" data-testid={VIP.groupTiktok} className="flex items-center gap-2 bg-pink-500/15 text-pink-300 border border-pink-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-pink-500/25">
                            <Music2 className="w-4 h-4" strokeWidth={1.8} /> TikTok
                          </a>
                        )}
                        {group.discord_group_url && (
                          <a href={group.discord_group_url} target="_blank" rel="noreferrer" data-testid={VIP.groupDiscord} className="flex items-center gap-2 bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-indigo-500/25">
                            <Users className="w-4 h-4" strokeWidth={1.8} /> Discord Grup
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-white/40">Tautan grup belum diatur oleh admin.</p>
                    )}
                  </div>
                </section>
              )}

              <section>
                <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Program VIP</h3>
                {programs.length === 0 ? (
                  <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada program VIP aktif.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {programs.map((p) => (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={VIP.programCard}
                        onClick={() => setDetailProgram(p)}
                        className="text-left bg-white/[0.02] border border-white/10 rounded-2xl p-5 transition-colors hover:border-cyan-400/40 hover:bg-white/[0.04] cursor-pointer"
                      >
                        <p className="text-white font-medium">{p.name}</p>
                        <p className="text-sm text-white/50 mt-1 line-clamp-3 whitespace-pre-line">{p.description}</p>
                        <div className="text-xs text-white/40 mt-3 space-y-1">
                          <p>Min. Equity: ${p.min_equity.toLocaleString()}</p>
                          <p>Min. Exist Equity: ${p.min_exist_equity.toLocaleString()}</p>
                          <p>Max. Inaktif: {p.max_inactive_days} hari</p>
                        </div>
                        <span className="inline-block mt-3 text-xs text-cyan-400 font-medium">Baca selengkapnya →</span>
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Ajukan VIP</h3>
                {hasActiveFlow ? (
                  <div className="bg-cyan-400/5 border border-cyan-400/20 rounded-2xl p-6 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
                    <p className="text-white/70 text-sm">
                      {isActiveVip
                        ? 'Selamat! Status VIP Anda aktif.'
                        : 'Anda memiliki pengajuan VIP yang sedang diproses. Menunggu keputusan admin.'}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid={VIP.applyForm} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-4">
                    <div>
                      <label className={labelClass}>Program VIP <span className="text-red-400">*</span></label>
                      <select className={inputClass} data-testid="vip-program-select" value={form.vip_program_id} onChange={(e) => setForm({ ...form, vip_program_id: e.target.value })}>
                        {programs.map((p) => (
                          <option key={p.id} value={p.id} className="bg-[#090D1A]">{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Broker <span className="text-red-400">*</span></label>
                        {brokers.length > 0 ? (
                          <div className="flex gap-2 items-center">
                            {(() => { const s = brokers.find((b) => b.name === form.broker_name); return s?.logo_url ? <img src={s.logo_url} alt={s.name} className="w-9 h-9 rounded-lg object-contain bg-[#050914] border border-white/10 shrink-0" data-testid="vip-broker-logo" /> : null; })()}
                            <select className={inputClass} data-testid="vip-broker-select" value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })}>
                              <option value="" className="bg-[#090D1A]">Pilih broker...</option>
                              {brokers.map((b) => (
                                <option key={b.id} value={b.name} className="bg-[#090D1A]">{b.name}</option>
                              ))}
                            </select>
                            {(() => {
                              const sel = brokers.find((b) => b.name === form.broker_name);
                              return sel?.referral_url ? (
                                <a href={sel.referral_url} target="_blank" rel="noreferrer" data-testid="vip-broker-referral" title="Daftar via referral" className="shrink-0 flex items-center gap-1 bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 px-3 rounded-lg text-sm font-medium hover:bg-cyan-400/20 transition-colors">
                                  Daftar
                                </a>
                              ) : null;
                            })()}
                          </div>
                        ) : (
                          <input className={inputClass} value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })} placeholder="Exness / IC Markets" />
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>No. Akun <span className="text-red-400">*</span></label>
                        <input className={inputClass} value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelClass}>Nama Akun <span className="text-red-400">*</span></label>
                        <input className={inputClass} value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelClass}>Equity (USD) <span className="text-red-400">*</span></label>
                        <input type="number" className={inputClass} value={form.submitted_equity} onChange={(e) => setForm({ ...form, submitted_equity: Number(e.target.value) })} />
                      </div>
                      <div>
                        <label className={labelClass}>Username TradingView <span className="text-red-400">*</span></label>
                        <input className={inputClass} value={form.tradingview_username} onChange={(e) => setForm({ ...form, tradingview_username: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelClass}>Bukti (screenshot)</label>
                        <ImageUpload value={proofUrl} onUploaded={setProofUrl} label="Unggah Bukti (opsional)" testId="vip-proof-upload" />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Catatan</label>
                      <textarea className={inputClass} rows={2} value={form.member_note} onChange={(e) => setForm({ ...form, member_note: e.target.value })} />
                    </div>
                    <button
                      type="submit"
                      data-testid={VIP.applySubmit}
                      disabled={submitting || formInvalid}
                      title={formInvalid ? (missingField() || '') : ''}
                      className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
                      Ajukan VIP
                    </button>
                    <p className="text-[11px] text-white/40 mt-2">Kolom bertanda <span className="text-red-400">*</span> wajib diisi. Bukti screenshot & catatan opsional.</p>
                  </form>
                )}
              </section>
            </div>

            {/* History */}
            <div className="lg:col-span-1">
              <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Riwayat Pengajuan</h3>
              {applications.length === 0 ? (
                <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada pengajuan.</p>
              ) : (
                <div className="space-y-3">
                  {applications.map((a) => (
                    <div key={a.id} data-testid={VIP.applicationRow} className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/70">{a.broker_name || 'Pengajuan VIP'}</span>
                        <StatusBadge status={a.status} />
                      </div>
                      <p className="text-xs text-white/40">Equity: ${Number(a.submitted_equity).toLocaleString()}</p>
                      {a.admin_note && <p className="text-xs text-white/50 mt-2 border-t border-white/10 pt-2">Admin: {a.admin_note}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!detailProgram} onOpenChange={(o) => { if (!o) setDetailProgram(null); }}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="vip-program-detail">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /> {detailProgram?.name}
            </DialogTitle>
          </DialogHeader>
          {detailProgram && (
            <div className="space-y-4">
              <p className="text-sm text-white/70 whitespace-pre-line leading-relaxed">{detailProgram.description || 'Tidak ada keterangan.'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm border-t border-white/10 pt-4">
                <div><p className="text-xs text-white/40 uppercase tracking-wider">Min. Equity</p><p className="text-white">${detailProgram.min_equity.toLocaleString()}</p></div>
                <div><p className="text-xs text-white/40 uppercase tracking-wider">Min. Exist Equity</p><p className="text-white">${detailProgram.min_exist_equity.toLocaleString()}</p></div>
                <div><p className="text-xs text-white/40 uppercase tracking-wider">Max. Inaktif</p><p className="text-white">{detailProgram.max_inactive_days} hari</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
